import { convexQuery } from "@convex-dev/react-query";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  formatAddress,
  hasRequiredAddressFields,
} from "@plasma/utils/format-address";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Coffee,
  Edit3,
  Plus,
  Trash2,
  Utensils,
} from "lucide-react";
import { useState } from "react";

import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";

const locationsGetAllMineRef = api.locations.getAllMine;

const deleteLocationRef = api.locations.deleteLocation;

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

export function LocationsTable() {
  const navigate = useNavigate();

  const [showErrorLabel, setShowErrorLabel] = useState(false);

  const { data: locations } = useQuery(convexQuery(locationsGetAllMineRef, {}));

  const deleteLocationMutation = useMutation({
    mutationFn: (data: { id: Id<"locations"> }) =>
      convexClient.mutation(deleteLocationRef, data),
    onSuccess: (_result, variables) => {
      // Track location deletion in PostHog.
      ProductEvents.locationDeleted(variables.id);
    },
  });

  const handleRedirect = () => {
    if (canContinue) {
      // Track onboarding completion in PostHog (PIN creation = step 1, location = step 2)
      ProductEvents.onboardingCompleted(2);
      void navigate({ to: "/" });
      return;
    }
    setShowErrorLabel(true);
  };

  if (locations === undefined) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  const canContinue = locations.length >= 1;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Address</TableHead>
            <TableHead>Phone Number</TableHead>
            <TableHead>Style</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {locations.map((location) => (
            <TableRow key={location.id}>
              <TableCell>
                <div className="text-foreground text-weight-medium flex items-center gap-3">
                  <img
                    alt={`${location.name} logo`}
                    className="size-9 rounded-full"
                    height={36}
                    src={
                      location.logoUrl ??
                      "/images/location-logo-placeholder.svg"
                    }
                    width={36}
                  />

                  {location.name}
                </div>
              </TableCell>
              <TableCell>
                {hasRequiredAddressFields(location)
                  ? formatAddress(location)
                  : "Address not set"}
              </TableCell>
              <TableCell>{location.phoneNumber}</TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  {location.style === "QSR" ? <Coffee /> : <Utensils />}
                  {location.type}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-3">
                  {/* V4.1: Route-based edit for URL context tracking */}
                  <Button
                    onClick={() =>
                      navigate({ to: `/locations/${location.id}/edit` })
                    }
                    size="icon"
                    type="button"
                    variant="secondary"
                  >
                    <Edit3 className="size-5" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" type="button" variant="destructive">
                        <Trash2 className="size-5" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove location</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove this location?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="bg-muted flex items-center justify-start gap-3 rounded-2xl px-5 py-4">
                        <img
                          alt={`${location.name} logo`}
                          className="size-10 rounded-full"
                          height={40}
                          src={
                            location.logoUrl ??
                            "/images/location-logo-placeholder.svg"
                          }
                          width={40}
                        />
                        <div className="space-y-1">
                          <p className="label text-foreground leading-tight">
                            {location.name}
                          </p>
                          <p className="body-sm text-foreground leading-tight">
                            {hasRequiredAddressFields(location)
                              ? formatAddress(location)
                              : "Address not set"}
                          </p>
                        </div>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            deleteLocationMutation.mutate({
                              id: location.id,
                            });
                          }}
                          variant="destructive"
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={5}>
              {/* V4.1: Route-based create for URL context tracking */}
              <Button
                onClick={() => navigate({ to: "/locations/new" })}
                type="button"
                variant="secondary"
              >
                <Plus className="size-4" />
                Add location
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <div className="flex flex-col items-center justify-center space-y-4">
        <Button
          className="w-full min-w-115 gap-1.5"
          id="redirectButton"
          onClick={handleRedirect}
          size="lg"
          type="button"
        >
          Start exploring
          <ArrowRight className="size-4" />
        </Button>
        {!canContinue && showErrorLabel && (
          <Label
            className="body-sm text-destructive leading-5"
            htmlFor="redirectButton"
          >
            Hold on! Please add at least one location to get started.
          </Label>
        )}
      </div>
    </>
  );
}
